import { TopBannerOnboarding } from '@client/components/TopBannerOnboarding';
import { useToast } from '@client/components/hooks/use-toast';
import { ProgressBarStepItem } from '@client/components/ltc-financial-planning/ProgressBarStepItem';
import { useLtcFinancialPlanningData } from '@client/components/ltc-financial-planning/hooks/useLtcFinancialPlanningData';
import {
  PROGRESS_STEPS,
  STEP_VALIDATION,
  useLtcFinancialPlanningNavigation,
} from '@client/components/ltc-financial-planning/hooks/useLtcFinancialPlanningNavigation';
import { Button } from '@client/components/ui/button';
import { selectClient, selectLtcFinancialPlanning } from '@client/model/selectors';
import { AppState } from '@client/model/store';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContinueWithDeductions } from './hooks/useContinueWithDeductions';

// Import step components
import { FullScreenLoadingOverlay } from '@/components/FullScreenLoadingOverlay';
import BottomNavigation from '@/components/ltc-financial-planning/BottomNavigation';
import { useNavigate } from 'react-router';
import { AssetDetailsStep } from './steps/AssetDetailsStep';
import { AssetSelectionStep } from './steps/AssetSelectionStep';
import { ComparisonStep } from './steps/ComparisonStep';
import { CoverageAssessmentStep } from './steps/CoverageAssessmentStep';
import { PoliciesStep } from './steps/PoliciesStep';
import { StepContentProps } from './types';

const stepComponentMap: Record<LtcFinancialPlanningStep, React.ComponentType<StepContentProps>> = {
  'asset-selection': AssetSelectionStep,
  'asset-details': AssetDetailsStep,
  'coverage-assessment': CoverageAssessmentStep,
  'policies': PoliciesStep,
  'comparison': ComparisonStep,
};

export function LtcFinancialPlanning({ clientId }: ClientIdProps) {
  const navigation = useLtcFinancialPlanningNavigation();
  const { toast, dismiss } = useToast();
  const [toastId, setToastId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { ltcFinancialPlanningState, isLoading } = useLtcFinancialPlanningData(clientId);
  const client = useSelector(selectClient);
  const ltcPlanningState = useSelector(selectLtcFinancialPlanning);
  const { handleContinueWithDeductions, isSaving } = useContinueWithDeductions();

  // Handle invalid/old step names by redirecting to first step
  const StepComponent = stepComponentMap[navigation.currentStep];
  const currentProgressStep = navigation.progressBarStep;

  // Get validation for current step
  const currentValidation = STEP_VALIDATION[navigation.currentStep];

  // If step is invalid, redirect to first step
  if (!StepComponent || !currentValidation) {
    navigation.goToStep('asset-selection');
    return null;
  }

  // Validate current step - construct partial state inline to avoid Redux DevTools warning
  const isStepValid = currentValidation.validate(ltcFinancialPlanningState, {
    client: { data: client },
  } as AppState);

  // Dismiss toast when step becomes valid
  useEffect(() => {
    if (isStepValid && toastId) {
      dismiss(toastId);
      setToastId(null);
    }
  }, [isStepValid, dismiss, toastId]);

  const handleContinue = () => {
    if (!isStepValid) {
      const result = toast({
        title: 'Action Required',
        description: currentValidation.errorMessage,
        variant: 'default',
        className: 'border-yellow-500 bg-yellow-50 text-yellow-800',
      });
      setToastId(result.id);
      return;
    }

    handleContinueWithDeductions();
  };

  const handleBack = () => {
    navigation.goPrev();
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <FullScreenLoadingOverlay isLoading={isLoading || isSaving || ltcPlanningState.isLoading} />
      {/* Header with Logo */}
      <div className="bg-white px-4 py-4 md:px-8">
        <TopBannerOnboarding
          className="px-2 md:px-0"
          actionButton={
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/clients/${clientId}/onboarding/funding-sources`)}
              className="mt-1"
            >
              Switch to Old Financing Flow
            </Button>
          }
        />
      </div>

      {/* Progress Bar */}
      <nav
        aria-label="Progress"
        className="px-4 py-6 md:px-8"
      >
        <ol className="mx-auto flex max-w-2xl items-center justify-center gap-3">
          {PROGRESS_STEPS.map(step => (
            <ProgressBarStepItem
              key={step.id}
              step={step}
              currentProgressStep={currentProgressStep}
              currentStep={navigation.currentStep}
            />
          ))}
        </ol>
      </nav>

      {/* Main Content */}
      <main className="flex-1 px-4 pb-32 md:px-8">
        <div className="mx-auto max-w-4xl">
          <StepComponent navigation={navigation} />
        </div>
      </main>

      <BottomNavigation
        navigation={navigation}
        onContinue={handleContinue}
        onBack={handleBack}
        canContinue={isStepValid}
      />
    </div>
  );
}
